import type {
  CreateCredentialRequest,
  CreateNotifierRequest,
  DeleteCredentialRequest,
  GetCredentialSecretResponse,
  ListCredentialsResponse,
  ListNotifiersResponse,
  TestCredentialKeysRequest,
  UpdateCredentialRequest,
  UpdateNotifierRequest,
} from '@fleece/models';
import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';
import { useApi } from '@/hooks/use-connection';

const CREDENTIALS = ['settings', 'credentials'] as const;
const NOTIFIERS = ['settings', 'notifiers'] as const;

/**
 * Every few seconds while the page is open: a feed connects, reconnects or fails without
 * anyone touching the page, and a stale "connected" is the reading that misleads.
 */
const FEED_POLL_MS = 5_000;

export function useCredentials(): UseQueryResult<ListCredentialsResponse> {
  const api = useApi();
  return useQuery({ queryKey: CREDENTIALS, queryFn: () => api.listCredentials(), refetchInterval: FEED_POLL_MS });
}

export function useNotifiers(): UseQueryResult<ListNotifiersResponse> {
  const api = useApi();
  return useQuery({ queryKey: NOTIFIERS, queryFn: () => api.listNotifiers() });
}

/** A write, then the list it changed read again, so the page shows what the service stored rather than what was sent. */
function useWrite<T, R>(key: ReadonlyArray<string>, write: (request: T) => Promise<R>): UseMutationResult<R, Error, T> {
  const queryClient = useQueryClient();
  return useMutation({ mutationFn: write, onSettled: () => queryClient.invalidateQueries({ queryKey: key }) });
}

export function useCreateCredential() {
  const api = useApi();
  return useWrite(CREDENTIALS, (request: CreateCredentialRequest) => api.createCredential(request));
}

export function useUpdateCredential() {
  const api = useApi();
  return useWrite(CREDENTIALS, (request: UpdateCredentialRequest) => api.updateCredential(request));
}

export function useDeleteCredential() {
  const api = useApi();
  return useWrite(CREDENTIALS, (request: DeleteCredentialRequest) => api.deleteCredential(request));
}

/** Asks the provider about a saved credential's keys; the answer is recorded, so the list is read again. */
export function useTestCredential() {
  const api = useApi();
  return useWrite(CREDENTIALS, (credentialId: string) => api.testCredential({ credentialId }));
}

/** Asks the provider about keys typed into a form. Nothing is stored, so nothing is read again. */
export function useTestCredentialKeys() {
  const api = useApi();
  return useMutation({ mutationFn: (request: TestCredentialKeysRequest) => api.testCredentialKeys(request) });
}

/**
 * A credential's keys, in plain text. Read when something shows them and dropped from the
 * cache as soon as nothing does, so they are not left in the page's memory to be found.
 */
export function useCredentialSecret(credentialId: string): UseQueryResult<GetCredentialSecretResponse> {
  const api = useApi();
  return useQuery({
    queryKey: ['settings', 'credential-secret', credentialId],
    queryFn: () => api.getCredentialSecret({ credentialId }),
    gcTime: 0,
    staleTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

export function useCreateNotifier() {
  const api = useApi();
  return useWrite(NOTIFIERS, (request: CreateNotifierRequest) => api.createNotifier(request));
}

export function useUpdateNotifier() {
  const api = useApi();
  return useWrite(NOTIFIERS, (request: UpdateNotifierRequest) => api.updateNotifier(request));
}

export function useDeleteNotifier() {
  const api = useApi();
  return useWrite(NOTIFIERS, (notifierId: string) => api.deleteNotifier({ notifierId }));
}

export function useTestNotifier() {
  const api = useApi();
  return useMutation({ mutationFn: (notifierId: string) => api.testNotifier({ notifierId }) });
}
